import React, { useEffect, useState } from "react";
import { X, CheckCircle2 } from "lucide-react";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../../../lib/utils";
import { microLabel, accentBarSection } from "../../../../lib/design-system";
import { closeATR } from "../../../../services/program.service";

// Phase 6a — Compact modal for closing an open ATR.
// Just asks for closureNotes + optional new attainment level.

const inputBase =
  "w-full px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-accent1 transition-colors";

const CloseAtrForm = ({ atr, onClose }) => {
  const [closureNotes, setClosureNotes] = useState("");
  const [closedAttainment, setClosedAttainment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape" && !submitting) onClose(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose, submitting]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!closureNotes.trim()) {
      setError("Closure notes are required — describe what was actually done.");
      return;
    }
    setSubmitting(true);
    try {
      await closeATR(atr._id, {
        closureNotes: closureNotes.trim(),
        closedAttainment:
          closedAttainment === "" ? null : Number(closedAttainment),
      });
      toast.success("ATR closed");
      onClose(true);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Close failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        onClick={() => !submitting && onClose(false)}
      >
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-md w-full max-w-xl"
        >
          <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <div className={microLabel}>Close ATR</div>
              <h2 className="mt-1 text-2xl font-black tracking-tight text-gray-900 dark:text-white">
                Close: {atr?.outcome?.code} ATR
              </h2>
              <div className={cn(accentBarSection, "mt-3")} />
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400 max-w-md">
                Closure notes become Criterion 8 evidence — what action was
                actually taken, when, and the outcome. Be specific.
              </p>
            </div>
            <button
              onClick={() => onClose(false)}
              disabled={submitting}
              className="p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-5">
            <div>
              <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1.5">
                Closure Notes *
              </label>
              <textarea
                value={closureNotes}
                onChange={(e) => setClosureNotes(e.target.value)}
                rows={5}
                required
                placeholder="Lab manual revised on 12 Mar 2026; 3 new design tasks added. TA training completed 18 Mar. Re-assessment in May 2026 showed improvement in CO3 from L1 to L2…"
                className={cn(inputBase, "resize-y")}
              />
            </div>

            <div>
              <label className="block text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1.5">
                Closed Attainment Level (0–3)
              </label>
              <input
                type="number"
                min={0}
                max={3}
                step={0.01}
                value={closedAttainment}
                onChange={(e) => setClosedAttainment(e.target.value)}
                placeholder="e.g. 2.3 (the level after intervention)"
                className={inputBase}
              />
              <p className="mt-1 text-xs text-gray-400">
                Optional but recommended — captures the year-over-year
                improvement for trend reporting.
              </p>
            </div>

            {error && (
              <div className="p-3 rounded-sm border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => onClose(false)}
                disabled={submitting}
                className="px-4 py-2 rounded-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-semibold transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className={cn(
                  "inline-flex items-center gap-2 px-5 py-2 rounded-sm bg-emerald-600 text-white font-semibold transition-opacity",
                  submitting
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:opacity-90"
                )}
              >
                <CheckCircle2 className="h-4 w-4" />
                {submitting ? "Closing…" : "Mark Closed"}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CloseAtrForm;
